import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AltioraContact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.company || !formData.industry || !formData.service) {
      toast({
        title: "Please fill all required fields",
        description: "Name, email, company, industry, and service are required",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact/submit`, formData);
      
      if (response.data.status === 'success') {
        setSubmitted(true);
        toast({
          title: "Request Submitted Successfully!",
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again or contact us directly at contact@altiora.co.in",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-50 to-teal-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get in Touch</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Schedule your free consultation today and discover how we can optimize your financial operations
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        {submitted && (
          <div className="max-w-2xl mx-auto mb-8">
            <Card className="border-2 border-green-500 bg-green-50">
              <CardContent className="p-6 flex items-center gap-4">
                <CheckCircle2 className="w-12 h-12 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-1">Request Submitted!</h3>
                  <p className="text-green-800">We'll contact you within 24 hours at {formData.email}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <a href="mailto:contact@altiora.co.in" className="text-gray-600 hover:text-teal-600">
                          contact@altiora.co.in
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Phone</p>
                        <p className="text-gray-600">US: +1 (669) 245-6417</p>
                        <p className="text-gray-600">India: +91-9752582205</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Office</p>
                        <p className="text-gray-600">India Operations</p>
                        <p className="text-gray-600 text-sm">Serving US Clients</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-gray-900">Availability</p>
                        <p className="text-gray-600">24/7 Global Support</p>
                        <p className="text-gray-600 text-sm">Always here for you</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-teal-50 to-blue-50 border-2 border-teal-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">Why Reach Out?</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ Free consultation with experts</li>
                  <li>✓ Custom solutions for your business</li>
                  <li>✓ Competitive pricing quotes</li>
                  <li>✓ Fast response within 24 hours</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Request Free Consultation</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-semibold">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="border-2"
                        placeholder="John Doe"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-semibold">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="border-2"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-semibold">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="border-2"
                        placeholder="+1 (XXX) XXX-XXXX"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company" className="font-semibold">Company Name *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        className="border-2"
                        placeholder="Your Company Inc."
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="industry" className="font-semibold">Industry *</Label>
                      <Select onValueChange={(value) => handleSelectChange('industry', value)} required>
                        <SelectTrigger className="border-2">
                          <SelectValue placeholder="Select your industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="it">Information Technology</SelectItem>
                          <SelectItem value="ecommerce">E-commerce</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="service" className="font-semibold">Service Interested In *</Label>
                      <Select onValueChange={(value) => handleSelectChange('service', value)} required>
                        <SelectTrigger className="border-2">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ar">Accounts Receivable (AR)</SelectItem>
                          <SelectItem value="ap">Accounts Payable (AP)</SelectItem>
                          <SelectItem value="claims">Claims Recovery</SelectItem>
                          <SelectItem value="customer">Customer Services</SelectItem>
                          <SelectItem value="all">All Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-semibold">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="border-2"
                      placeholder="Tell us about your business needs and how we can help..."
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-6 text-lg"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit Consultation Request'}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                  
                  <p className="text-sm text-gray-600 text-center">
                    We'll respond within 24 hours to schedule your free consultation
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AltioraContact;
